import { z } from 'zod';
import { DEFAULT_CRM_SETTINGS, DUE_PAYMENT_MODES, REPORT_IDS, SEGMENTS } from '../constants/crm.js';
import { isoDate, objectId, optional, pagination, requiredText, text } from './common.js';

const paise = z.coerce.number().int().min(0).max(100_000_000_00);

// ---------------------------------------------------------------- settings (Settings → Customers & reports)
export const loyaltySettingsSchema = z.strictObject({
  enabled: z.boolean(),
  earnPerRupees: z.coerce.number().int().min(1).max(100_000),
  pointValuePaise: z.coerce.number().int().min(1).max(100_000),
  minRedeemPoints: z.coerce.number().int().min(0).max(1_000_000),
  expiryMonths: z.coerce.number().int().min(0).max(120),
});
export const segmentSettingsSchema = z.strictObject({
  newDays: z.coerce.number().int().min(1).max(365),
  lapsedDays: z.coerce.number().int().min(7).max(730),
  regularVisits: z.coerce.number().int().min(2).max(100),
  highValueSpendPaise: paise,
});
export const feedbackSettingsSchema = z.strictObject({
  enabled: z.boolean(),
  notifyBelow: z.coerce.number().int().min(1).max(5),
});
export const reportSettingsSchema = z.strictObject({
  dailySummaryEmail: z.boolean(),
  dailySummaryHour: z.coerce.number().int().min(0).max(23),
});
const d = DEFAULT_CRM_SETTINGS;
export const crmSettingsFields = {
  loyalty: loyaltySettingsSchema.default({ ...d.loyalty }),
  segments: segmentSettingsSchema.default({ ...d.segments }),
  feedback: feedbackSettingsSchema.default({ ...d.feedback }),
  reports: reportSettingsSchema.default({ ...d.reports }),
};

// ---------------------------------------------------------------- customers
export const monthDay = z
  .string()
  .trim()
  .regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'Use MM-DD, e.g. 08-15');
export const customerProfileFields = {
  birthday: optional(monthDay),
  anniversary: optional(monthDay),
  tags: z.array(z.string().trim().toLowerCase().min(1).max(30)).max(10).default([]),
  notes: optional(text(500)),
};

export const customerListQuery = z.strictObject({
  q: optional(text(60)),
  segment: z.enum(SEGMENTS.map((s) => s.key)).optional(),
  tag: optional(text(30)),
  consent: z.enum(['1', '0']).optional(),
  due: z.enum(['1']).optional(),
  birthdays: z.enum(['week', 'month']).optional(),
  sort: z.enum(['recent', 'spend', 'visits', 'name', 'points']).default('recent'),
  ...pagination,
});

export const duesCollectSchema = z.strictObject({
  amountPaise: paise.refine((v) => v > 0, 'Enter an amount'),
  mode: z.enum(DUE_PAYMENT_MODES),
  reference: optional(text(60)),
  note: optional(text(200)),
});

export const loyaltyAdjustSchema = z.strictObject({
  points: z.coerce
    .number()
    .int()
    .refine((v) => v !== 0, 'Enter points to add or remove')
    .refine((v) => Math.abs(v) <= 1_000_000, 'Too many'),
  note: requiredText(200),
});

export const feedbackInput = z.strictObject({
  rating: z.coerce.number().int().min(1, 'Choose 1–5 stars').max(5),
  comment: optional(text(500)),
});

/**
 * Campaign channels. Free: "whatsapp" (click-to-chat links sent one by one from the shop's phone)
 * and "email" (through the business's Zoho mail). "sms" needs a paid provider (DLT in India).
 */
export const CAMPAIGN_CHANNELS = Object.freeze([
  { key: 'whatsapp', label: 'WhatsApp (free, send one by one)' },
  { key: 'email', label: 'Email (free, from your email account)' },
  { key: 'sms', label: 'SMS (coming soon)', comingSoon: true },
  { key: 'whatsapp_api', label: 'WhatsApp, sent automatically (coming soon)', comingSoon: true },
]);

export const campaignAudienceSchema = z.strictObject({
  segment: z.enum(SEGMENTS.map((s) => s.key)).optional(),
  tag: optional(text(30)),
  channel: z.enum(CAMPAIGN_CHANNELS.map((c) => c.key)),
  subject: optional(text(120)),
  message: optional(text(1000)),
});

export const unsubscribeTokenParam = z.strictObject({ token: z.string().trim().min(10).max(80) });

// ---------------------------------------------------------------- reports
export const reportQuery = z
  .strictObject({
    from: isoDate,
    to: isoDate,
    outletId: z.union([objectId, z.literal('all')]).optional(),
    format: z.enum(['json', 'csv', 'xlsx', 'pdf']).default('json'),
    stockItemId: optional(objectId),
    limit: z.coerce.number().int().min(1).max(50_000).optional(),
  })
  .refine((q) => q.from <= q.to, { message: 'From must be before To', path: ['from'] });

export const reportIdParam = z.strictObject({ id: z.enum(REPORT_IDS) });

export const dashboardQuery = z.strictObject({
  outletId: z.union([objectId, z.literal('all')]).optional(),
  date: optional(isoDate),
});
